'use strict';

import WebMessengerAPI from './messenger/embed.js';
import Widget from './widget/embed.js';
import { parse as parseQs } from './common/qs';
import { decodeJSONFromBase64 } from './common/decoder';

// The widget data should be included on the script
// tag inside the `data-fxo-widget` attribute.
// If this isn't present, fail!
const scriptElements = document.querySelectorAll('script[data-fxo-widget]');
if (!scriptElements.length) {
  throw new Error(
    'Script was not added correctly. Ensure the script contains data under the `data-fxo-widget` attribute.'
  );
}

const callbacks = [];
const messengers = window.FxoMessengers || {};
scriptElements.forEach((scriptElement) => {
  // widgetData is a base64 encoded,
  // stringified JSON object.
  let widgetData = decodeJSONFromBase64(
    scriptElement.getAttribute('data-fxo-widget')
  );
  if (!widgetData) {
    throw new Error('Widget data was invalid.');
  }
  const id = scriptElement.id || widgetData.web.botId || 'unknown';
  if (window.FxoMessengers && window.FxoMessengers[id]) {
    console.log('Messenger already loaded (' + id + ')');
    return;
  }

  // // If we received an older version of the web config,
  // // transform it into the newer one
  // if (widgetData.web && widgetData.web.data) {
  //   const webConfig = decodeJSONFromBase64(widgetData.web.data);
  //   if (!webConfig) {
  //     throw new Error('Messenger data was invalid.');
  //   }
  //   widgetData.web = webConfig;
  // }

  let hideWelcomeMessageDuration = scriptElement.getAttribute(
    'data-hide-welcome-message-days'
  );
  if (hideWelcomeMessageDuration) {
    widgetData.hideWelcomeForDays = hideWelcomeMessageDuration;
  }
  let welcomeRevealAfterSecs = scriptElement.getAttribute(
    'data-reveal-welcome-message-seconds'
  );
  if (welcomeRevealAfterSecs) {
    widgetData.welcomeRevealAfterSecs = welcomeRevealAfterSecs;
  }
  let welcomeHideAfterSecs = scriptElement.getAttribute(
    'data-hide-welcome-message-seconds'
  );
  if (welcomeHideAfterSecs) {
    widgetData.welcomeHideAfterSecs = welcomeHideAfterSecs;
  }
  let themeUrl = scriptElement.getAttribute('data-theme-css-url');
  let customUrl = scriptElement.getAttribute('data-custom-css-url');
  let themeName = scriptElement.getAttribute('data-theme-name');
  let themeCssOnly = scriptElement.getAttribute('data-theme-css-only');
  let widgetIconUrl = scriptElement.getAttribute('data-widget-icon-url');
  let targetElementSelector = scriptElement.getAttribute(
    'data-target-element-selector'
  );
  let widgetPosition = scriptElement.getAttribute('data-widget-position');
  let embedMode = scriptElement.getAttribute('data-embed-mode') || 'sidebar';
  if (themeUrl) {
    widgetData.themeCssUrl = themeUrl;
  }
  if (customUrl) {
    widgetData.customCssUrl = customUrl;
  }
  if (themeName) {
    widgetData.themeName = themeName;
  }
  if (themeCssOnly) {
    widgetData.themeCssOnly = !!themeCssOnly;
  }
  if (widgetIconUrl) {
    widgetData.widgetIconUrl = widgetIconUrl;
  }
  if (widgetPosition) {
    widgetData.widgetPosition = widgetPosition;
  }

  let widget;
  if (!targetElementSelector && embedMode === 'sidebar') {
    // We have our widget data.
    // Configure the widget.
    widget = new Widget(widgetData);
  }

  // Set up the web messenger.
  const messenger = new WebMessengerAPI(id);

  // We only set the botId, allowBackgroundScroll and theme css here. Everything else can be fetched
  // from the botmaster.
  messenger.setConfig({
    botId: widgetData.web.botId,
    allowBackgroundScroll: widgetData.allowBackgroundScroll,
    popupHeight: widgetData.popupHeight,
    themeCssUrl: widgetData.themeCssUrl,
    customCssUrl: widgetData.customCssUrl,
    themeName: widgetData.themeName,
    themeCssOnly: widgetData.themeCssOnly,
    widgetPosition: widgetData.widgetPosition,
  });

  let overlay, overlayContainer;
  function showMessenger(mode, targetElement) {
    if (mode === 'sidebar') {
      return messenger.toggle();
    } else if (mode === 'inline') {
      messenger.setConfig({ mode: 'fullscreen', standalone: true });
      messenger.create(targetElement);
    } else if (mode === 'overlay') {
      if (overlay) {
        Object.assign(overlay.style, { display: 'block' });
      } else {
        overlay = document.createElement('div');
        overlay.id = 'fxo-overlay';
        Object.assign(overlay.style, {
          'width': '100%',
          'height': '100%',
          'background-color': 'rgba(0,0,0,.8)',
          'z-index': '9998',
          'position': 'fixed',
          'left': 0,
          'top': 0,
        });
        overlay.addEventListener('click', () => {
          messenger.destroy();
        });

        overlayContainer = document.createElement('div');
        overlayContainer.id = 'fxo-overlay-chat-container';
        Object.assign(overlayContainer.style, {
          'border': 'none',
          'position': 'fixed',
          'top': '50%',
          'left': '50%',
          'transform': 'translateX(-50%) translateY(-50%)',
          'width': '100%',
          'max-width': '1024px',
          'height': '100%',
          'max-height': '800px',
        });
        overlay.appendChild(overlayContainer);

        messenger.setConfig({
          mode: 'fullscreen',
        });
      }
      document.body.appendChild(overlay);
      document.body.classList.add('fxo-messenger-noscroll');
      messenger.create(overlayContainer);
    }
  }

  function hideMessenger() {
    if (overlay) {
      Object.assign(overlay.style, { display: 'none' });
      document.body.classList.remove('fxo-messenger-noscroll');
    }
  }

  if (widget) {
    widget.on('revealWebMessenger', () => {
      //console.log('revealWebMessenger');
      showMessenger('sidebar');
    });
    messenger.on('destroyed', () => {
      widget.sendMessageToWidget('widget:collapse');
    });

    widget.render();
    // And expose it.
    window.FxoWidget = widget.getPublicAPI();
  } else if (targetElementSelector) {
    const targetElement = document.querySelector(targetElementSelector);
    if (!targetElement) {
      console.log(
        'No target element with the selector ' +
          targetElementSelector +
          ' could be found. Your Flow XO bot cannot be embedded when the data-target-element attribute is set but does not exist.'
      );
      return;
    }
    if (embedMode === 'sidebar') {
      messenger.on('destroyed', () => {
        targetElement.classList.remove('fxo-messenger-active');
      });
      targetElement.addEventListener('click', () => {
        showMessenger('sidebar');
        targetElement.classList.add('fxo-messenger-active');
      });
    } else if (embedMode === 'inline') {
      showMessenger('inline', targetElement);
    }
    if (embedMode === 'overlay') {
      targetElement.addEventListener('click', () => {
        showMessenger('overlay');
      });
      messenger.on('destroyed', () => {
        hideMessenger();
      });
    }
  }

  // If we have a callback, then call it.
  const qs = parseQs(scriptElement.src);
  const callback = qs.callback || widgetData.callback;
  if (callback && window[callback]) {
    callbacks.push(callback);
  }
  messengers[id] = messenger;
  window.FxoMessenger = window.FxoMessenger || messenger;
});

window.FxoMessengers = messengers;
callbacks.forEach((callback) => {
  window[callback]();
});



// WEBPACK FOOTER //
// ./src/embed.js